import React from "react";
import FileIcon from "./FileIcon";
import { HiOutlineDotsVertical, HiOutlineShare, HiDownload, HiOutlineTrash } from "react-icons/hi";
import { motion } from "framer-motion";
import axios from "axios";
import { downloadFileAPI } from "../services/driveService";

const FileCard = ({ file, view = "grid", isBig = false, onView, onRename, onShare, onDelete }) => {
  const isImage = ["jpg", "jpeg", "png", "gif", "svg"].includes(file?.extension?.toLowerCase());

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleDownload = async (e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem("dbToken");
      const url = downloadFileAPI(file.id);

      const response = await axios.get(url, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const urlObject = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = urlObject;
      link.download = file?.name || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(urlObject);
    } catch (err) {
      console.error("Download failed:", err);
      alert("Download failed. Please try again.");
    }
  };

  if (view === "list") {
    return (
      <motion.div
        layout
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={onView}
        className="flex items-center justify-between p-4 bg-white/40 hover:bg-white border-b border-slate-100 hover:shadow-lg transition-all cursor-pointer group rounded-xl mb-1"
      >
        <div className="flex items-center gap-4 flex-1">
          <div className="p-2 rounded-lg bg-slate-100 group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-colors">
            <FileIcon extension={file.extension} className="w-5 h-5" />
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-slate-700 truncate max-w-[200px] md:max-w-xs tracking-tight">
              {file.name}
            </span>
            <span className="text-[10px] text-slate-400 font-medium md:hidden uppercase tracking-wider">
              {formatFileSize(file.size)} • {file.date}
            </span>
          </div>
        </div>

        <div className="hidden md:flex flex-1 text-xs font-semibold text-slate-500 uppercase tracking-widest">
          {file.date}
        </div>

        <div className="hidden md:flex flex-[0.5] text-xs font-bold text-indigo-500 bg-indigo-50 px-2 py-1 rounded-lg justify-center w-fit">
          {formatFileSize(file.size)}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); onShare(file); }}
            className="!p-2 !bg-transparent !border-none !w-auto !h-auto hover:!bg-indigo-50 rounded-full text-slate-400 opacity-0 group-hover:opacity-100 transition-all hover:text-indigo-600"
            title="Share"
          >
            <HiOutlineShare />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onRename(file); }}
            className="!p-2 !bg-transparent !border-none !w-auto !h-auto hover:!bg-indigo-50 rounded-full text-slate-400 opacity-0 group-hover:opacity-100 transition-all hover:text-indigo-600"
            title="Rename"
          >
            <HiOutlineDotsVertical />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(file); }}
            className="!p-2 !bg-transparent !border-none !w-auto !h-auto hover:!bg-red-50 rounded-full text-slate-400 opacity-0 group-hover:opacity-100 transition-all hover:text-red-500"
            title="Delete"
          >
            <HiOutlineTrash />
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ y: -8, shadow: "0 25px 50px -12px rgba(0,0,0,0.15)" }}
      onClick={onView}
      className={`glass-card-premium p-0 overflow-hidden cursor-pointer group ${isBig ? "rounded-[3rem]" : ""}`}
    >
      {/* Thumbnail Area */}
      <div className={`${isBig ? "h-64" : "h-36"} w-full bg-slate-100/50 flex items-center justify-center relative overflow-hidden transition-all duration-500`}>
        {isImage ? (
          <div className="w-full h-full bg-indigo-50 flex items-center justify-center group-hover:scale-110 transition-transform duration-700">
            <FileIcon extension={file.extension} className={`${isBig ? "w-32 h-32" : "w-16 h-16"} opacity-20 filter blur-xl absolute`} />
            <FileIcon extension={file.extension} className={`${isBig ? "w-32 h-32" : "w-16 h-16"} relative z-10 drop-shadow-2xl`} />
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center group-hover:scale-110 transition-transform duration-700">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-500/5" />
            <FileIcon extension={file.extension} className={`${isBig ? "w-32 h-32" : "w-16 h-16"} drop-shadow-xl relative z-10`} />
          </div>
        )}

        {/* Quick Actions Overlay */}
        <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center p-2 z-20">
          <div className="flex flex-wrap justify-center gap-1 sm:gap-1.5 p-1.5 bg-white/95 backdrop-blur-md rounded-2xl shadow-lg translate-y-2 group-hover:translate-y-0 transition-transform duration-300 max-w-full">
            <button
              onClick={handleDownload}
              className="p-1.5 hover:bg-indigo-50 rounded-full text-indigo-600 transition-all hover:scale-110 shrink-0"
              title="Download"
            >
              <HiDownload size={15} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onShare(file); }}
              className="p-1.5 hover:bg-slate-100 rounded-full text-slate-600 transition-all hover:scale-110 shrink-0"
              title="Share"
            >
              <HiOutlineShare size={15} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onRename(file); }}
              className="p-1.5 hover:bg-slate-100 rounded-full text-slate-600 transition-all hover:scale-110 shrink-0"
              title="Rename"
            >
              <HiOutlineDotsVertical size={15} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(file); }}
              className="p-1.5 hover:bg-red-50 rounded-full text-red-500 transition-all hover:scale-110 shrink-0"
              title="Delete"
            >
              <HiOutlineTrash size={15} />
            </button>
          </div>
        </div>
      </div>

      {/* Info Area */}
      <div className={`${isBig ? "p-6" : "p-4"} bg-white/80 border-t border-white/40 transition-all`}>
        <h4 className={`${isBig ? "text-lg mb-2" : "text-sm mb-1"} font-bold text-slate-800 truncate tracking-tight pr-2`}>
          {file.displayName}
        </h4>
        <div className="flex items-center justify-between">
          <div className={`flex items-center gap-1.5 ${isBig ? "text-xs" : "text-[10px]"} font-bold text-slate-400 uppercase tracking-tighter`}>
            <span className="text-indigo-500 px-1.5 py-0.5 bg-indigo-50 rounded italic">{file.extension}</span>
            <span>•</span>
            <span>{formatFileSize(file.size)}</span>
          </div>
          {file.shared && <HiOutlineShare size={isBig ? 16 : 12} className="text-slate-300" />}
        </div>
      </div>
    </motion.div>
  );
};

export default FileCard;
